import { generateWithMeta } from '~/services/AIContent';

import { reportError } from '~/utils/reportError';

import type { FlashOfferRoomDraft } from './utils';

const STANDARD_CREATIVITY = 2;

export type GenerateRoomDescription = (roomDraft: FlashOfferRoomDraft) => Promise<string>;

export function buildRoomDescriptionGenerator(propertyName?: string | null): GenerateRoomDescription {
  return async (roomDraft) => {
    const response = await generateWithMeta(
      {
        model: 'room-type',
        field: 'description',
        propertyName,
        roomName: roomDraft.name,
        amenities: roomDraft.amenities,
      },
      STANDARD_CREATIVITY,
    );

    if (response.error || !response.result) {
      throw new Error(response.error?.message ?? 'No room description returned');
    }

    return response.result;
  };
}

// Description is required to create a room, so every blank draft gets the AI's suggestion; the CM can still edit or regenerate it.
export async function fillRoomDescriptions(
  roomDrafts: Array<FlashOfferRoomDraft>,
  generateRoomDescription: GenerateRoomDescription,
): Promise<{ hasFailed: boolean; roomDrafts: Array<FlashOfferRoomDraft> }> {
  let hasFailed = false;

  const filledDrafts = await Promise.all(
    roomDrafts.map(async (roomDraft) => {
      if (roomDraft.description?.trim() || !roomDraft.name.trim()) {
        return roomDraft;
      }

      try {
        return { ...roomDraft, description: await generateRoomDescription(roomDraft) };
      } catch (error) {
        reportError(error);
        hasFailed = true;
        return roomDraft;
      }
    }),
  );

  return { hasFailed, roomDrafts: filledDrafts };
}
